import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import api from '../services/api';
import { useFamily } from '../contexts/FamilyContext';

const NamePicker = () => {
  const navigate = useNavigate();
  const { currentFamily } = useFamily();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  const fetchGroups = useCallback(async () => {
    if (!currentFamily) return;
    try {
      const response = await api.get(`/name-picker/?family_id=${currentFamily.id}`);
      setGroups(response.data.results || response.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }, [currentFamily]);

  useEffect(() => {
    if (currentFamily) {
      fetchGroups();
    } else {
      setLoading(false);
    }
  }, [currentFamily, fetchGroups]);

  const handleCreate = async () => {
    if (!currentFamily) {
      alert('Please select or create a family first');
      return;
    }
    try {
      const response = await api.post('/name-picker/', {
        ...formData,
        family_id: currentFamily.id,
      });
      setGroups([response.data, ...groups]);
      setOpen(false);
      setFormData({ name: '', description: '' });
      navigate(`/name-picker/${response.data.id}`);
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to create group');
    }
  };

  const copyShareLink = (shareToken) => {
    const url = `${window.location.origin}/name-picker/join/${shareToken}`;
    navigator.clipboard.writeText(url);
    alert('Share link copied to clipboard!');
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Name Picker Groups</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
        >
          Create Group
        </Button>
      </Box>

      {!currentFamily ? (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              No Family Selected
            </Typography>
            <Typography>
              Please create or join a family to start creating name picker groups.
            </Typography>
            <Button
              variant="contained"
              sx={{ mt: 2 }}
              onClick={() => navigate('/families')}
            >
              Go to Families
            </Button>
          </CardContent>
        </Card>
      ) : loading ? (
        <Typography>Loading...</Typography>
      ) : groups.length === 0 ? (
        <Card>
          <CardContent>
            <Typography>No groups yet. Create your first name picker group!</Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {groups.map((group) => (
            <Grid item xs={12} md={6} key={group.id}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="start">
                    <Box>
                      <Typography variant="h5" gutterBottom>
                        {group.name}
                      </Typography>
                      {group.description && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {group.description}
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        {group.participant_count} participants
                      </Typography>
                      {group.is_drawn && (
                        <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                          ✓ Names drawn
                        </Typography>
                      )}
                    </Box>
                    <Tooltip title="Copy share link">
                      <IconButton
                        onClick={() => copyShareLink(group.share_token)}
                        size="small"
                      >
                        <ContentCopyIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    onClick={() => navigate(`/name-picker/${group.id}`)}
                  >
                    View
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Name Picker Group</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Group Name"
            margin="normal"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
          <TextField
            fullWidth
            label="Description"
            margin="normal"
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default NamePicker;
